import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, Plus, Search, X, Filter, ChevronDown, ChevronUp,
  Download, Upload, FileText, FileSpreadsheet, FileOutput, List, LayoutList,
  LayoutGrid, Layers, RefreshCcw, Clock, CheckCircle, XCircle, Hash, Percent, Package, MapPin,
  Tag, TrendingUp, Shield
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { useTranslation } from 'react-i18next';
import { getBrandCountryOptions } from '../../shared/utils/brandCountry';
import { getBrandLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {sliceQawaemStats, QAWAEM_KPI_GRID_CLASS, QAWAEM_TABLE_ACTIONS_TH_CLASS, QawaemTableSelectAllHeader, QawaemTableSelectRowCell, createArticleStatusChangeHandler, mapStatusOptionsToColumnFilter, useQawaemColumnVisibility, QawaemTableStickyActionsTd, sliceArticlesListItems } from './articlesListPageUtils';
import { useArticlesConfig, useArticlesListViewMode } from './useArticlesConfig';
import { fetchCatalogProducts } from './catalogProductOptions';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { buildArticleStatusSelectOptions } from './articleCatalogStatusOptions';

const NS = 'modules.article.pages.classificationDouanierePage';

const ClassificationDouanierePage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const articlesConfig = useArticlesConfig();
  const defaultOriginSamples = qawaem
    ? [p('country_saudi'), p('country_china'), p('country_uae')]
    : [p('country_algeria'), p('country_china'), p('country_france')];
  const countryOptions = useMemo(() => getBrandCountryOptions((key) => p(key)), [t]);
  const [records, setRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewMode, setViewMode] = useArticlesListViewMode(articlesConfig);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_classifications');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'التصنيف الجمركي' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'تصنيف جمركي' });

  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);

  const [formData, setFormData] = useState({
    reference_number: '',
    hs_code: '',
    description: '',
    duty_rate: '',
    origin_country: '',
    product_id: '',
    product_name: '',
    status: '',
    notes: ''
  });

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const statusFilterRef = useRef(null);

  useEffect(() => {
    loadRecords();
    loadProducts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/api/articles/classification-douaniere');
      const data = response?.data ?? response;
      setRecords(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading customs classifications:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      setProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords(selectedRecords.includes(id)
      ? selectedRecords.filter(rId => rId !== id)
      : [...selectedRecords, id]
    );
  };

  

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.hs_code?.trim()) {
      window.alert(p('hs_code_required'));
      return;
    }
    try {
      setLoading(true);
      const payload = {
        ...formData,
        duty_rate: parseFloat(formData.duty_rate) || 0
      };
      if (selectedRecord) {
        await localApiAdapter.put(`/api/articles/classification-douaniere/${selectedRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/api/articles/classification-douaniere', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadRecords();
      resetForm();
    } catch (error) {
      console.error('Error saving classification:', error);
      notifyError(showNotify, p('save_error', { defaultValue: 'تعذّر الحفظ' }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      reference_number: record.reference_number || '',
      hs_code: record.hs_code || '',
      description: record.description || '',
      duty_rate: record.duty_rate?.toString() || '',
      origin_country: record.origin_country || '',
      product_id: record.product_id || '',
      product_name: record.product_name || '',
      status: record.status || '',
      notes: record.notes || ''
    });
    setShowModal(true);
  };

  

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(selectedRecords);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        setLoading(true);
        await localApiAdapter.delete(`/api/articles/classification-douaniere/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        await loadRecords();
      } else {
        const deleteCount = ids.length;
        await Promise.all(ids.map(id =>
          localApiAdapter.delete(`/api/articles/classification-douaniere/${id}`)
        ));
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        setSelectedRecords([]);
        loadRecords();
      }
    } catch (error) {
      console.error('Error deleting classification:', error);
        notifyError(showNotify, p('save_error', { defaultValue: 'تعذّر الحذف' }));
      } finally {
        setLoading(false);
    }
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      reference_number: '',
      hs_code: '',
      description: '',
      duty_rate: '',
      origin_country: '',
      product_id: '',
      product_name: '',
      status: '',
      notes: ''
    });
    setSelectedRecord(null);
    setShowModal(false);
  };

  const openNewModal = () => {
    setSelectedRecord(null);
    setFormData({
      reference_number: '',
      hs_code: '',
      description: '',
      duty_rate: '',
      origin_country: '',
      product_id: '',
      product_name: '',
      status: '',
      notes: ''
    });
    setShowModal(true);
  };

  const recordsArray = Array.isArray(records) ? records : [];
  const distinctHsCodes = new Set(recordsArray.map(r => r.hs_code).filter(Boolean)).size;
  const classifiedProducts = recordsArray.filter(r => r.product_id).length;
  const avgDutyRate = recordsArray.length > 0
    ? recordsArray.reduce((sum, r) => sum + (parseFloat(r.duty_rate) || 0), 0) / recordsArray.length
    : 0;

  const stats = {
    total: recordsArray.length,
    active: recordsArray.filter(r => r.status === 'actif').length,
    distinctHs: distinctHsCodes,
    avgDutyRate: avgDutyRate.toFixed(1),
    classifiedProducts
  };

  const originCountries = [...new Set(recordsArray.map(r => r.origin_country).filter(Boolean))];

  const classificationDistribution = {
    all_classifications: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'actives', name: p('dist_actives'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'inactives', name: p('dist_inactives'), count: recordsArray.filter(r => r.status === 'inactif').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'with_product', name: p('dist_with_product'), count: stats.classifiedProducts, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    },
    by_origin: {
      title: p('dist_by_origin'),
      data: (originCountries.length > 0 ? originCountries.slice(0, 6) : defaultOriginSamples).map((country, i) => ({
        name: country,
        count: recordsArray.filter(r => r.origin_country === country).length,
        color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-green-500 to-green-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-amber-500 to-amber-600', 'bg-gradient-to-r from-red-500 to-red-600', 'bg-gradient-to-r from-cyan-500 to-cyan-600'][i % 6]
      }))
    },
    by_status: {
      title: p('dist_by_status'),
      data: [
        { filterKey: 'actif', name: t('common.active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'inactif', name: t('common.inactive'), count: recordsArray.filter(r => r.status === 'inactif').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'revision', name: p('status_revision'), count: recordsArray.filter(r => r.status === 'revision').length, color: 'bg-gradient-to-r from-amber-500 to-amber-600' }
      ]
    }
  };

  const distributionInfo = classificationDistribution[mainViewMode] || classificationDistribution.all_classifications;

  const mainTabs = [
    { id: 'all_classifications', label: t('common.general_view'), icon: LayoutGrid },
    { id: 'by_origin', label: p('tab_by_origin'), icon: MapPin },
    { id: 'by_status', label: t('common.by_status'), icon: Shield }
  ];

  const quickFilterButtons = [
    { id: 'all', label: t('common.all'), icon: Layers },
    { id: 'actif', label: p('dist_actives'), icon: CheckCircle },
    { id: 'inactif', label: p('dist_inactives'), icon: XCircle },
    { id: 'with_product', label: p('quick_with_product'), icon: Package }
  ];

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 'actif', label: t('common.active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
      { value: 'inactif', label: t('common.inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
      { value: 'revision', label: p('status_revision'), icon: TrendingUp, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    ],
    [t, p],
  );

  const statusColumnFilterOptions = useMemo(
    () => mapStatusOptionsToColumnFilter(STATUS_OPTIONS),
    [STATUS_OPTIONS],
  );

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler('/api/articles/classification-douaniere', loadRecords),
    [],
  );

  const getStatusBadge = (status) => {
    const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>
        <Icon size={14} />
        {label}
      </span>
    );
  };

  const baseFilteredRecords = useMemo(
    () =>
      recordsArray.filter((record) => {
        if (dateRange.start || dateRange.end) {
          const recordDate = new Date(record.created_at || 0);
          if (dateRange.start) {
            const startOfDay = new Date(dateRange.start);
            startOfDay.setHours(0, 0, 0, 0);
            if (recordDate < startOfDay) return false;
          }
          if (dateRange.end) {
            const endOfDay = new Date(dateRange.end);
            endOfDay.setHours(23, 59, 59, 999);
            if (recordDate > endOfDay) return false;
          }
        }

        if (statusFilter === 'actif' && record.status !== 'actif') return false;
        if (statusFilter === 'inactif' && record.status !== 'inactif') return false;

        if (quickFilter === 'actif' && record.status !== 'actif') return false;
        if (quickFilter === 'inactif' && record.status !== 'inactif') return false;
        if (quickFilter === 'with_product' && !record.product_id) return false;

        if (activeDistributionFilter) {
          if (mainViewMode === 'all_classifications') {
            if (activeDistributionFilter === 'actives' && record.status !== 'actif') return false;
            if (activeDistributionFilter === 'inactives' && record.status !== 'inactif') return false;
            if (activeDistributionFilter === 'with_product' && !record.product_id) return false;
          }
          if (mainViewMode === 'by_origin' && record.origin_country !== activeDistributionFilter) return false;
          if (mainViewMode === 'by_status' && record.status !== activeDistributionFilter) return false;
        }

        const matchesSearch =
          !searchTerm ||
          record.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.hs_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.origin_country?.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesSearch;
      }),
    [recordsArray, dateRange, statusFilter, quickFilter, activeDistributionFilter, mainViewMode, searchTerm],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('created_at', (r) =>
        r.created_at ? new Date(r.created_at).toLocaleDateString(dateLocale) : '',
      ),
      textColumnRule('reference_number', (r) => r.reference_number || ''),
      textColumnRule('hs_code', (r) => r.hs_code || ''),
      textColumnRule('duty_rate', (r) => String(parseFloat(r.duty_rate) || 0)),
      textColumnRule('product_name', (r) => r.product_name || ''),
      textColumnRule('description', (r) => r.description || ''),
      textColumnRule('origin_country', (r) => r.origin_country || ''),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [dateLocale],
  );

  const filteredRecords = useMemo(
    () => applyColumnFilters(baseFilteredRecords, colFilters, columnFilterRules),
    [baseFilteredRecords, colFilters, columnFilterRules],
  );

  const filteredRecordIds = useMemo(() => filteredRecords.map((r) => r.id), [filteredRecords]);
  const displayedRecords = useMemo(
    () => sliceArticlesListItems(filteredRecords, articlesConfig.itemsPerPage),
    [filteredRecords, articlesConfig.itemsPerPage],
  );
  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;
  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) setSelectedRecords([]);
    else setSelectedRecords(filteredRecordIds);
  };

  const productOptions = products.map(p => ({
    value: p.id,
    label: p.reference ? `${p.name} (${p.reference})` : p.name,
    isDefault: true
  }));

  const originOptions = [
    ...originCountries.map(c => ({ value: c, label: c, isDefault: true })),
    ...countryOptions.filter((opt) => !originCountries.includes(opt.value)),
  ];

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'created_at', 'reference_number', 'hs_code', 'description', 'duty_rate', 'origin_country', 'product_name', 'status',
  ]);

  const qawaemColumns = useMemo(
    () => [
      { key: 'created_at', label: p('col_created_at'), checked: columnVisibility.created_at !== false },
      { key: 'reference_number', label: p('col_reference'), checked: columnVisibility.reference_number !== false, required: true },
      { key: 'hs_code', label: p('col_hs_code'), checked: columnVisibility.hs_code !== false, required: true },
      { key: 'description', label: p('col_description'), checked: columnVisibility.description !== false },
      { key: 'duty_rate', label: p('col_duty_rate'), checked: columnVisibility.duty_rate !== false },
      { key: 'origin_country', label: p('col_origin_country'), checked: columnVisibility.origin_country !== false },
      { key: 'product_name', label: p('col_product'), checked: columnVisibility.product_name !== false },
      { key: 'status', label: t('common.status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: t('common.actions'), checked: true, required: true },
    ],
    [t, p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  return (
    <div className="space-y-6 employees-container">
      {/* Header */}
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Globe className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadRecords} className="refresh-button" title={t('common.refresh')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={openNewModal}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_classification')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={QAWAEM_KPI_GRID_CLASS}>
        {sliceQawaemStats([
          { label: t('common.total'), value: stats.total, icon: Globe, color: 'from-blue-500 to-blue-600' },
          { label: p('stat_actives'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { label: p('stat_distinct_hs'), value: stats.distinctHs, icon: Hash, color: 'from-purple-500 to-purple-600' },
          { label: p('stat_avg_rate'), value: `${stats.avgDutyRate}%`, icon: Percent, color: 'from-amber-500 to-amber-600' },
          { label: p('stat_classified_products'), value: stats.classifiedProducts, icon: Package, color: 'from-cyan-500 to-cyan-600' }
        ], 4).map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.label}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} style={{ pointerEvents: 'none' }} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6 overflow-hidden"
          >
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('main_view')}</h3>
              <div className="flex flex-wrap gap-2">
                {mainTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      mainViewMode === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {mainViewMode === 'all_classifications' && (
              <div>
                <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('quick_filters')}</h3>
                <div className="flex flex-wrap gap-2">
                  {quickFilterButtons.map(btn => (
                    <button
                      key={btn.id}
                      onClick={() => setQuickFilter(btn.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                        quickFilter === btn.id
                          ? 'bg-purple-500/30 text-purple-200 border-2 border-purple-400/50'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      <btn.icon className="h-4 w-4" />
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white/80 text-sm font-semibold uppercase tracking-wider">{distributionInfo.title}</h3>
                {activeDistributionFilter && (
                  <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                    <XCircle size={14} />{p('reset_filter')}
                  </button>
                )}
              </div>
              <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>
                {distributionInfo.data.map((item, index) => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  const filterKey = item.filterKey || item.name;
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setActiveDistributionFilter(activeDistributionFilter === filterKey ? null : filterKey)}
                      className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left w-full ${
                        activeDistributionFilter === filterKey ? 'border-blue-400/60 bg-blue-500/20' : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-1.5">
                        <span className="block w-full text-white/70 text-sm font-medium text-right">{item.name}</span>
                        <span className="text-white font-semibold">{item.count} ({percentage}%)</span>
                      </div>
                      <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          className={`h-full ${item.color} rounded-full`}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} />
                  {showCol('created_at') ? <TableColumnHeaderFilter columnKey="created_at" label={p('col_created_at')} value={colFilters.created_at ?? ''} onChange={(v) => setColFilter('created_at', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('reference_number') ? <TableColumnHeaderFilter columnKey="reference_number" label={p('col_reference')} value={colFilters.reference_number ?? ''} onChange={(v) => setColFilter('reference_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('hs_code') ? <TableColumnHeaderFilter columnKey="hs_code" label={p('col_hs_code')} value={colFilters.hs_code ?? ''} onChange={(v) => setColFilter('hs_code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('description') ? <TableColumnHeaderFilter columnKey="description" label={p('col_description')} value={colFilters.description ?? ''} onChange={(v) => setColFilter('description', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('duty_rate') ? <TableColumnHeaderFilter columnKey="duty_rate" label={p('col_duty_rate')} value={colFilters.duty_rate ?? ''} onChange={(v) => setColFilter('duty_rate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('origin_country') ? <TableColumnHeaderFilter columnKey="origin_country" label={p('col_origin_country')} value={colFilters.origin_country ?? ''} onChange={(v) => setColFilter('origin_country', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('product_name') ? <TableColumnHeaderFilter columnKey="product_name" label={p('col_product')} value={colFilters.product_name ?? ''} onChange={(v) => setColFilter('product_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  <QawaemTableHeaderActionsCell label={t('common.actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS} />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60">{t('common.loading')}</td></tr>
                ) : displayedRecords.length > 0 ? (
                  displayedRecords.map((record, index) => (
                    <tr
                      key={record.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {showCol('created_at') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {record.created_at ? new Date(record.created_at).toLocaleDateString(dateLocale) : 'N/A'}
                            </div>
                          </div>
                        </td>
                      ) : null}
                      {showCol('reference_number') ? (
                        <td className="px-6 py-4"><span className="text-white font-mono font-semibold">{record.reference_number || 'N/A'}</span></td>
                      ) : null}
                      {showCol('hs_code') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold font-mono">{record.hs_code || 'N/A'}</span></div></td>
                      ) : null}
                      {showCol('description') ? (
                        <td className="px-6 py-4"><span className="text-white/80 text-sm line-clamp-2">{record.description || '—'}</span></td>
                      ) : null}
                      {showCol('duty_rate') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.duty_rate != null ? `${record.duty_rate}%` : 'N/A'}</span></td>
                      ) : null}
                      {showCol('origin_country') ? (
                        <td className="px-6 py-4"><div className="flex items-center gap-1.5 text-white/80"><MapPin size={14} className="text-amber-400" />{record.origin_country || 'N/A'}</div></td>
                      ) : null}
                      {showCol('product_name') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{record.product_name || p('not_linked')}</span></div></td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={STATUS_OPTIONS} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })} />
                          </div>
                        </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => handleView(record)}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Globe size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
              {filteredRecords.length > 0 && (
                <tfoot className="bg-white/5 border-t border-white/10">
                  <tr>
                    <td colSpan="5" className="px-6 py-3 text-white/60 text-sm font-medium">
                      {p('footer_displayed', { count: filteredRecords.length })}
                    </td>
                    <td className="px-6 py-3 text-white font-semibold text-sm">
                      {p('footer_avg')}: {(filteredRecords.reduce((s, r) => s + (parseFloat(r.duty_rate) || 0), 0) / filteredRecords.length).toFixed(1)}%
                    </td>
                    <td colSpan="4" className="px-6 py-3 text-white/60 text-sm text-right">
                      {p('footer_classified', { count: filteredRecords.filter(r => r.product_id).length })}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* Cards View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedRecords.length > 0 ? (
              displayedRecords.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Globe size={20} className="text-blue-400" />
                          {record.reference_number || 'N/A'}
                        </h3>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 font-mono">
                          HS {record.hs_code}
                        </span>
                        <QawaemClickableStatusBadge
                          status={record.status}
                          recordId={record.id}
                          options={STATUS_OPTIONS}
                          onChange={handleStatusChange}
                          title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_identification')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('col_hs_code')}:</span> {record.hs_code}</div>
                            <div><span className="font-medium">{p('col_description')}:</span> {record.description || 'N/A'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_tariff')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Percent size={16} className="text-amber-400" />
                              <span className="font-medium">{p('label_rate')}:</span> {record.duty_rate != null ? `${record.duty_rate}%` : 'N/A'}
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_origin_product')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <MapPin size={16} className="text-red-400" />
                              {record.origin_country || 'N/A'}
                            </div>
                            <div className="flex items-center gap-2">
                              <Package size={16} className="text-green-400" />
                              {record.product_name || p('not_linked')}
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_notes')}</h5>
                          <p className="text-sm text-white/60">{record.notes || p('no_notes')}</p>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(record)}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Globe size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Globe className="h-6 w-6 text-blue-400" />
                {selectedRecord ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="classification-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {selectedRecord ? p('update') : t('common.create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="classification-form" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reference')}</label>
                    <input
                      type="text"
                      value={formData.reference_number}
                      onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                      placeholder={p('placeholder_reference')}
                      dir="ltr"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                      <RequiredLabelText text={p('col_hs_code')} required />
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.hs_code}
                      onChange={(e) => setFormData({ ...formData, hs_code: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                      placeholder={p('placeholder_hs_code')}
                      dir="ltr"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_duty_rate')}</label>
                    <CustomNumberInput
                      step={0.01}
                      value={formData.duty_rate}
                      onChange={(v) => setFormData({ ...formData, duty_rate: v })}
                      placeholder={p('placeholder_duty_rate')}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_origin_country')}</label>
                    <CustomSelect
                      value={formData.origin_country || ''}
                      onChange={(val) => setFormData({ ...formData, origin_country: val || '' })}
                      options={countryOptions}
                      placeholder={p('placeholder_origin_country')}
                      searchable
                      creatable
                      autoSelect={false}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{t('common.status')}</label>
                    <CustomSelect
                      value={formData.status || ''}
                      onChange={(val) => setFormData({ ...formData, status: val || '' })}
                      options={buildArticleStatusSelectOptions(t)}
                      placeholder={p('placeholder_status')}
                      searchable={false}
                      creatable={false}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_associated_product')}</label>
                    <CustomSelect
                      value={formData.product_id}
                      onChange={(productId) => {
                        const product = products.find((prod) => prod.id === productId);
                        setFormData({
                          ...formData,
                          product_id: productId || '',
                          product_name: product?.name || formData.product_name,
                        });
                      }}
                      options={productOptions}
                      placeholder={p('placeholder_select_product')}
                      searchable
                      creatable={false}
                      autoSelect={false}
                    />
                  </div>
                  <div className="min-w-0 md:col-span-3">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_description')}</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder={p('placeholder_description')}
                    />
                  </div>
                  <div className="min-w-0 md:col-span-3">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_notes')}</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder={p('placeholder_notes')}
                    />
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && selectedRecord && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Globe className="h-6 w-6 text-blue-400" />
                {p('view_title')}
              </h2>
              <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors"><X className="h-6 w-6" /></button>
            </div>

            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Hash size={18} className="text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_hs')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_reference')}</p>
                    <p className="text-white font-semibold font-mono">{selectedRecord.reference_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_hs_code')}</p>
                    <p className="text-white font-semibold font-mono">{selectedRecord.hs_code || 'N/A'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_description')}</p>
                    <p className="text-white font-semibold">{selectedRecord.description || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Percent size={18} className="text-yellow-400" />
                    <h3 className="text-lg font-semibold text-white">{p('view_tariff')}</h3>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_duty_rate_short')}</p>
                    <p className="text-white font-bold text-xl">{selectedRecord.duty_rate != null ? `${selectedRecord.duty_rate}%` : 'N/A'}</p>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <MapPin size={18} className="text-red-400" />
                    <h3 className="text-lg font-semibold text-white">{p('view_origin_product')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_origin_country')}</p>
                      <p className="text-white font-semibold">{selectedRecord.origin_country || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_associated_product')}</p>
                      <p className="text-white font-semibold">{selectedRecord.product_name || p('not_linked')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{t('common.status')}</p>
                    {getStatusBadge(selectedRecord.status)}
                  </div>
                  <div className="text-right">
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_created_at')}</p>
                    <p className="text-white font-semibold">
                      {selectedRecord.created_at ? new Date(selectedRecord.created_at).toLocaleString(dateLocale) : 'N/A'}
                    </p>
                  </div>
                </div>
                {selectedRecord.notes && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_notes')}</p>
                    <p className="text-white/70 text-sm italic">{selectedRecord.notes}</p>
                  </div>
                )}
              </div>
            </div>

            <button onClick={() => setShowViewModal(false)} className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10">
              {t('common.close')}
            </button>
          </motion.div>
        </div>,
        document.body
      )}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default ClassificationDouanierePage;

